import { css, cx } from 'styled-system/css';
import { button } from 'styled-system/recipes';

import { ButtonArrow } from '@/rsc/ui/ButtonArrow';
import { Text } from '@/rsc/ui/Text';
import { localePath } from '@/utils/localePath';
import { GeneralPath } from '@/utils/models/page-paths';

const styles = {
  section: css({
    position: 'relative',
    isolation: 'isolate',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    bg: 'surface.500',
    color: 'text.500',
  }),
  // Huge outlined "404" behind the copy (decorative; the h1 carries the meaning).
  // With the fixed 80px header (global 404) vs alone in the viewport (route-level fallback).
  belowHeader: css({ minH: 'calc(100vh - 80px)' }),
  fullHeight: css({ minH: '100vh' }),
  bigCode: css({
    position: 'absolute',
    zIndex: -1,
    right: { base: '-6%', lg: '4%' },
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: { base: '11rem', sm: '18rem', lg: '26rem', '2xl': '32rem' },
    lineHeight: 1,
    fontWeight: 'bold',
    letterSpacing: '-0.04em',
    color: 'transparent',
    WebkitTextStroke: '1px rgba(255, 255, 255, 0.28)',
    userSelect: 'none',
    pointerEvents: 'none',
  }),
  // Slowly orbiting violet glow (keyframes `sh-404-orbit` in globals.css).
  glow: css({
    position: 'absolute',
    zIndex: -1,
    right: '-10%',
    top: '-10%',
    w: { base: '420px', lg: '720px' },
    h: { base: '420px', lg: '720px' },
    background: 'radial-gradient(50% 50% at 50% 50%, rgba(125, 84, 214, 0.35) 0%, rgba(125, 84, 214, 0) 100%)',
    animation: 'sh-404-orbit 14s linear infinite',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  }),
  content: css({
    w: '100%',
    maxW: '1920px',
    mx: 'auto',
    px: { base: 5, sm: 6, lg: 20, '3xl': 24 },
    py: { base: 16, sm: 24 },
  }),
  column: css({ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxW: '720px' }),
  eyebrow: css({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    px: 3,
    py: 1,
    mb: { base: 6, sm: 8 },
    bg: 'tag-bg',
    color: 'lime.500',
  }),
  heading: css({ color: 'text.500', mb: { base: 6, sm: 8 } }),
  desc: css({ color: 'text.300', maxW: '560px', mb: { base: 10, sm: 12 } }),
  // Layout only (the DS button styles come from the recipe): full width on mobile.
  button: css({ w: { base: '100%', xs: 'auto' } }),
};

export type NotFoundCopy = {
  title: string;
  page: string;
  underlined: string;
  desc: string;
  btn: string;
};

type Props = {
  copy: NotFoundCopy;
  locale: string;
  /** Rendered under the fixed site header (global 404) → leaves its 80px out of the min height. */
  withHeader?: boolean;
};

/**
 * The 404 section — presentational only (copy as props), so both the server 404 (NotFoundContent)
 * and the client fallback of app/[locale]/not-found.tsx render it. Imports stay client-safe and
 * small (the fallback is in every [locale] page's RSC payload): no `@/rsc/ui` barrel, no getT,
 * no images. The button is a plain <a> (a full navigation): the global 404 has its own <html>, and a
 * client-side transition from it into the [locale] root layout re-renders that layout in the browser
 * (React then warns about the layout's inline <script>).
 */
export const NotFoundSection = ({ copy, locale, withHeader }: Props) => (
  // data-testid: the e2e link crawl detects a 404 by it (locale-independent, unlike the copy).
  <section data-testid="not-found" className={cx(styles.section, withHeader ? styles.belowHeader : styles.fullHeight)}>
    <span className={styles.bigCode} aria-hidden>
      404
    </span>
    <div className={styles.glow} aria-hidden />
    <div className={styles.content}>
      <div className={styles.column}>
        <Text as="p" variant="text-sm/lineHeight-5/font-semibold" className={styles.eyebrow}>
          <span>404</span>
          <span aria-hidden>·</span>
          <span>{copy.title}</span>
        </Text>
        <Text
          as="h1"
          variant={{
            base: 'component-heading-large/2xl',
            sm: 'component-heading-large/3xl',
            lg: 'component-heading-large/4xl',
          }}
          className={styles.heading}
        >
          {copy.page} {copy.underlined}
        </Text>
        <Text
          variant={{ base: 'text-md/lineHeight-6/font-normal', lg: 'text-xl/lineHeight-7/font-normal' }}
          className={styles.desc}
        >
          {copy.desc}
        </Text>
        <a
          href={localePath(GeneralPath.HOME, locale)}
          className={cx(button({ variant: 'primary', tone: 'dark', size: 'lg' }), styles.button)}
        >
          {copy.btn}
          <ButtonArrow />
        </a>
      </div>
    </div>
  </section>
);
